import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Button } from '@accure/ui';
import { BotMessageSquare, X } from 'lucide-react';

import { LoadingText } from '@/ai/components/loading-text';
import { parseSSEChunk, parseStream } from '@/ai/components/utils';
import { GetDocumentSummary } from '@/api/fe-api/view/queries/ai/get-document-summary';
import {
  Dialog,
  DialogBody,
  type DialogProps,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip } from '@/components/ui/tooltip';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import type { Document } from '@/modules/documents/domain/entities/document';

export type DocumentSummaryDialogProps = {
  document: Document;
  onClose: () => void;
} & Omit<DialogProps, 'children'>;
export function DocumentSummaryDialog({
  document,
  onClose,
  ...props
}: DocumentSummaryDialogProps) {
  const summary = GetDocumentSummary.useQuery({
    documentId: document.file.name,
  });
  const cachedContent = GetDocumentSummary.useCachedContent({
    documentId: document.file.name,
  });
  const featureFlag = useFeatureFlag();
  const [streamedMessage, setStreamedMessage] = useState('');

  useEffect(() => {
    if (summary.data?.stream) {
      parseStream(summary.data.stream, setStreamedMessage);
    }
  }, [summary.data?.stream]);

  useEffect(() => {
    if (cachedContent) {
      setStreamedMessage(parseSSEChunk(cachedContent));
    }
  }, [cachedContent]);

  if (!featureFlag('ai')) {
    return null;
  }

  return (
    <Dialog
      {...props}
      onClose={() => {
        // Prevent default dialog close behavior
      }}
    >
      <DialogTitle className="flex justify-between">
        <div className="flex items-center gap-2">
          <Tooltip content="AI-generated summary">
            <BotMessageSquare className="text-neutral-500" />
          </Tooltip>
          {document.name}
        </div>
        <Tooltip content="Close" position="left" delay>
          <Button
            variant="ghost"
            className="text-neutral-400 hover:text-neutral-700"
            onClick={(e) => {
              onClose();
              e.stopPropagation();
            }}
          >
            <X />
          </Button>
        </Tooltip>
      </DialogTitle>
      <DialogBody>
        {summary.isLoading ? (
          <LoadingText
            isLoading={summary.isLoading}
            descriptions={[
              'Generating document summary...',
              'Analyzing content for key points...',
              'Preparing concise overview...',
            ]}
          />
        ) : (
          <div className="prose prose-neutral max-h-[80vh] max-w-none overflow-y-auto px-6">
            <ReactMarkdown>{streamedMessage}</ReactMarkdown>
          </div>
        )}
      </DialogBody>
    </Dialog>
  );
}
